import { watch } from 'vue';
import type { Ref } from 'vue';
import type { Router } from 'vue-router';
import type { IMenuSection } from '@fondationcss/ui';
import { menus } from '@/sidebar-items';
import { isEnabledUnlessTurnedOff } from '@/utils/student-portal-features';
import { resolveFeatureRedirect } from '@/utils/group-training-feature';
import { resolveDestination } from '@/utils/landing';

/** What the menu ACL reads of the signed-in user. */
export interface MenuViewer {
  permissions: readonly string[];
  modules: readonly string[];
  flags: Record<string, boolean>;
}

/** The gating keys a section, group, block or leaf may carry. */
interface MenuGate {
  module?: string;
  permissions?: string[];
  feature?: string;
}

/**
 * Mirrors the Fondation's menu `isAllowed`: module, then permissions, then feature. The feature
 * is read like the feature route guard reads it, so the two guards never disagree on a page.
 */
export function isMenuEntryAllowed(entry: MenuGate, viewer: MenuViewer): boolean {
  if (entry.module && !viewer.modules.includes(entry.module)) return false;
  if (entry.permissions && !entry.permissions.some(p => viewer.permissions.includes(p))) return false;
  return !entry.feature || isEnabledUnlessTurnedOff(viewer.flags, entry.feature);
}

/** Every `to` of a section in menu order, with the chain of entries that gates it. */
function entriesOf(section: IMenuSection): { to: string; gates: MenuGate[] }[] {
  const entries: { to: string; gates: MenuGate[] }[] = [];
  for (const group of section.groups) {
    if (group.to) entries.push({ to: group.to, gates: [section, group] });
    for (const block of group.items ?? []) {
      for (const leaf of block.items ?? []) {
        if (leaf.to) entries.push({ to: leaf.to, gates: [section, group, block, leaf] });
      }
    }
  }
  return entries;
}

/**
 * Where a section click should have taken the user. The Fondation opens a section on the first
 * `to` of its RAW groups, before any filtering, so a user whose rights stop short of that entry
 * lands on a page the menu hides from them. Gestion opens on the director's dashboard, whose
 * cards all answer 403 to someone who only manages enterprises (#1167); Enseignement opens on the
 * teacher dashboard, which the « Surveillant d'examen » cannot read either — they go on to
 * Présences, their only entry there (#1164).
 *
 * A section the menu does not show them at all is reached by an old link or an old landing — the
 * « Spécialiste » outside the roster landed in Enseignement until their own section (#1445). They
 * go where `/` sends them (utils/landing.ts), so the specialist reaches their section's dashboard,
 * which says « Fonctionnalité désactivée » itself when the view is off.
 *
 * Returns the first entry of the same section the user may see and reach, else their own landing
 * when the section is not theirs at all, or null when the landing is theirs, when `path` is no
 * section's landing, or when nothing else would do. `reachable` lets the caller skip a page
 * another guard would send straight back.
 */
export function sectionLandingRedirect(
  path: string,
  sections: readonly IMenuSection[],
  viewer: MenuViewer,
  reachable: (to: string) => boolean = () => true,
): string | null {
  const visible = (gates: MenuGate[]) => gates.every(gate => isMenuEntryAllowed(gate, viewer));
  for (const section of sections) {
    const entries = entriesOf(section);
    if (entries[0]?.to !== path) continue;
    if (visible(entries[0].gates)) return null;
    if (!visible([section])) {
      const own = resolveDestination(viewer.permissions);
      return own !== path && reachable(own) ? own : null;
    }
    return entries.find(entry => entry.to !== path && visible(entry.gates) && reachable(entry.to))?.to ?? null;
  }
  return null;
}

/**
 * Sends a user who lands on a section's opening page that the menu hides from them to the first
 * entry of that section they can see. Same installation pattern as `installFeatureRouteGuard`
 * (main.ts): the `beforeEach` judges every navigation, and the watch re-judges the standing
 * route once the profile and the flags have loaded, since a deep link can arrive before them.
 */
export function installSectionLandingGuard(
  router: Router,
  sources: {
    me: Ref<{ permissions: string[]; modules: string[] } | null | undefined>;
    flags: Ref<Record<string, boolean>>;
  },
): void {
  const redirectFor = (path: string): string | null => {
    const me = sources.me.value;
    if (!me) return null;
    const viewer: MenuViewer = { permissions: me.permissions, modules: me.modules, flags: sources.flags.value };
    // A page whose own flag is off would bounce back to its section's dashboard.
    const reachable = (to: string) => resolveFeatureRedirect(router.resolve(to), viewer.flags) === null;
    return sectionLandingRedirect(path, menus, viewer, reachable);
  };

  router.beforeEach((to) => {
    const target = redirectFor(to.path);
    return target ? { path: target } : true;
  });

  watch([sources.me, sources.flags], () => {
    const target = redirectFor(router.currentRoute.value.path);
    if (target) void router.replace({ path: target });
  }, { deep: true });
}
